// SSE Service for handling Server-Sent Events connection
class SSEService {
    constructor() {
        this.eventSource = null;
        this.clientId = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = window.CONFIG?.AUTH?.RETRY_DELAY || 5000;
        this.environment = window.Environment || null;
        this.listeners = new Map();
        this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
        this.rateLimited = false;
        this.connectionLimited = false;
        this.lastErrorCode = null;
    }

    /**
     * Initialize SSE connection with authentication and error handling
     */
    async connect() {
        // Idempotency: don't reconnect if already connected
        if (this.isConnected && this.eventSource) {
            // console.log('SSE: Already connected, skipping reconnect');
            return;
        }
        
        // Don't reconnect if currently connecting
        if (this.connectionState === 'connecting') {
            // console.log('SSE: Connection in progress, skipping reconnect');
            return;
        }
        
        try {
            // Refresh environment reference in case it wasn't available during construction
            if (!this.environment && window.Environment) {
                this.environment = window.Environment;
            }
            
            this.setConnectionState('connecting');
            await this.closeConnection();
            
            // Reset security flags
            this.rateLimited = false;
            this.connectionLimited = false;
            this.lastErrorCode = null;
            
            // Generate authentication parameters
            const authParams = await window.StyleManager.buildStyleConfig();
            this.clientId = authParams.clientId;
            
            // Build authenticated URL with obfuscated parameters
            const baseUrl = this.colorHexReceiver();
            const streamPath = this.decodeColorPattern();
            
            // Obfuscate auth parameters to hide sensitive data in URL
            const obfuscatedAuth = btoa(authParams.theme).replace(/[=]/g, '');
            const obfuscatedTimestamp = btoa(authParams.timestamp.toString()).replace(/[=]/g, '');
            const obfuscatedClientId = btoa(authParams.clientId).replace(/[=]/g, '');
            
            const url = `${baseUrl}/${streamPath}?a=${obfuscatedAuth}&t=${obfuscatedTimestamp}&c=${obfuscatedClientId}`;
            
            
            // Create EventSource connection directly (like working browser version)
            this.eventSource = new EventSource(url);
            this.setupEventHandlers();
            
        } catch (error) {
            // console.error('SSE connection failed:', error);
            this.handleConnectionFailure(error);
        }
    }
    
    /**
     * Test connection to detect HTTP errors before EventSource
     */
    async testConnection(url) {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            
            if (response.status === 429) {
                if (response.headers.get('content-type')?.includes('json')) {
                    const errorData = await response.json();
                    if (errorData.error?.includes('failed authentication attempts')) {
                        this.rateLimited = true;
                        throw new Error('Rate limited due to failed authentication attempts');
                    } else if (errorData.error?.includes('connection limit')) {
                        this.connectionLimited = true;
                        throw new Error('Connection limit reached');
                    }
                }
                throw new Error('Rate limited or connection limit reached');
            }
            
            if (response.status === 401) {
                throw new Error('Authentication failed');
            }
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
        } catch (error) {
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                // Network error, not HTTP error
                throw new Error('Network connection failed');
            }
            throw error;
        }
    }
    
    /**
     * Handle connection failures with appropriate error types
     */
    handleConnectionFailure(error) {
        this.setConnectionState('error');
        
        if (error.message.includes('Rate limited')) {
            this.rateLimited = true;
            this.emit('error', { message: 'Rate limited. Please wait before reconnecting.', error });
            this.scheduleReconnect(true);
        } else if (error.message.includes('Connection limit')) {
            this.connectionLimited = true;
            this.emit('error', { message: 'Connection limit reached. Close other connections.', error });
            this.scheduleReconnect(true);
        } else if (error.message.includes('Authentication failed')) {
            this.emit('error', { message: 'Authentication failed. Check credentials.', error });
            this.scheduleReconnect();
        } else {
            this.emit('error', { message: 'Failed to establish connection', error });
            this.scheduleReconnect();
        }
    }

    /**
     * Setup EventSource event handlers
     */
    setupEventHandlers() {
        if (!this.eventSource) return;

        this.eventSource.onopen = () => {
            this.setConnectionState('connected');
            this.reconnectAttempts = 0;
            this.emit('connected', { clientId: this.clientId });
        };

        this.eventSource.onmessage = (event) => {
            try {
                // Decompress message if needed
                const data = this.decompressMessage(event.data);
                this.handleMessage(data);
            } catch (error) {
                // console.error('Failed to parse SSE message:', error);
                this.emit('error', { message: 'Invalid message format', error });
            }
        };

        this.eventSource.onerror = (error) => {
            // console.error('SSE error:', error);
            
            // Check for HTTP error status codes
            if (error.target && error.target.readyState === EventSource.CLOSED) {
                // Try to determine error type from previous requests
                this.handleConnectionError();
            } else if (this.eventSource.readyState === EventSource.CONNECTING) {
                this.setConnectionState('connecting');
            }
        };
    }

    /**
     * Decompress SSE message if compressed (browser-compatible using pako)
     * @param {string} rawData - Raw SSE message data
     * @returns {Object} - Parsed message object
     */
    decompressMessage(rawData) {
        try {
            // Check if message is compressed (GZIP prefix)
            if (rawData.startsWith('GZIP:')) {
                // Extract base64 encoded data
                const encoded = rawData.substring(5);
                
                // Decode base64 to Uint8Array
                const binaryString = atob(encoded);
                const compressed = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    compressed[i] = binaryString.charCodeAt(i);
                }
                
                // Decompress with pako (browser-compatible gzip)
                const decompressed = pako.ungzip(compressed, { to: 'string' });
                
                // Parse JSON
                return JSON.parse(decompressed);
            } else {
                // Legacy: uncompressed JSON
                return JSON.parse(rawData);
            }
        } catch (error) {
            // console.error('SSE decompression failed:', error);
            throw new Error(`Decompression failed: ${error.message}`);
        }
    }

    /**
     * Handle incoming SSE messages
     * @param {Object} data - Parsed message data
     */
    handleMessage(data) {
        const { type, data: payload } = data;
        
        switch (type) {
            case 'all-trades':
                // Adapt trades from flat structure to nested enhanced_data
                const adaptedTrades = window.TradeDataAdapter ? 
                    window.TradeDataAdapter.adaptTrades(payload) : payload;
                // console.log(`📡 SSE received ${adaptedTrades?.length} historical trades from server`);
                this.emit('trades-data', adaptedTrades);
                break;
            case 'all-assets':
                this.emit('assets-data', payload);
                break;
            case 'enhanced-trades':
                this.emit('enhanced-trades-data', payload);
                break;
            case 'enhanced-assets':
                this.emit('enhanced-assets-data', payload);
                break;
            case 'market-context':
                this.emit('market-context-data', payload);
                break;
            case 'cluster-update':
                this.emit('cluster-data-update', payload);
                break;
            case 'trade-details':
                this.emit('trade-details', payload);
                break;
            case 'new-trades':
                // Adapt trades from flat structure to nested enhanced_data
                const adaptedNewTrades = window.TradeDataAdapter && payload?.trades ? 
                    window.TradeDataAdapter.adaptTrades(payload.trades) : payload?.trades;
                // console.log(`📡 SSE received ${adaptedNewTrades?.length || 0} new trades from server`);
                this.emit('new-trades-data', {
                    ...payload,
                    trades: adaptedNewTrades || payload?.trades
                });
                break;
            case 'connected':
                // console.log('📡 SSE connected to server', payload);
                this.emit('connected', payload);
                break;
            case 'heartbeat':
                this.handleHeartbeat(payload);
                break;
            case 'error':
                this.handleError(payload);
                break;
            case 'server-shutdown':
                this.handleServerShutdown(payload);
                break;
            default:
                // console.warn('Unknown SSE message type:', type);
        }
    }

    /**
     * Request trade details from server
     * @param {string|number} tradeId - Trade ID to fetch details for
     */
    async requestTradeDetails(tradeId) {
        if (!this.isConnected || !this.clientId) {
            throw new Error('SSE connection not established');
        }
        
        try {
            const baseUrl = this.colorHexReceiver();
            const url = `${baseUrl}/trade/${tradeId}?client=${this.clientId}`;
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
        } catch (error) {
            // console.error('Failed to request trade details:', error);
            this.emit('error', { message: 'Failed to fetch trade details', error });
        }
    }

    /**
     * Set connection state and update UI
     * @param {string} state - New connection state
     */
    setConnectionState(state) {
        const previousState = this.connectionState;
        this.connectionState = state;
        this.isConnected = state === 'connected';
        
        if (previousState !== state) {
            this.emit('state-change', { state, previousState });
        }
    }

    /**
     * Handle connection errors with proper error type detection
     */
    handleConnectionError() {
        // Check if we're rate limited or connection limited
        if (this.rateLimited) {
            this.setConnectionState('error');
            this.emit('error', { message: 'Rate limited. Please wait before reconnecting.' });
            this.scheduleReconnect(true); // Use longer delay
            return;
        }
        
        if (this.connectionLimited) {
            this.setConnectionState('error');
            this.emit('error', { message: 'Connection limit reached. Close other connections.' });
            this.scheduleReconnect(true);
            return;
        }
        
        // Standard connection error
        this.setConnectionState('error');
        this.emit('disconnected', { reason: 'Connection closed by server' });
        this.scheduleReconnect();
    }
    
    /**
     * Schedule reconnection attempt with security-aware delays
     */
    scheduleReconnect(isSecurityError = false) {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            // console.error('Max reconnection attempts reached');
            this.emit('error', { message: 'Connection failed after multiple attempts' });
            return;
        }
        
        this.reconnectAttempts++;
        
        // Use longer delays for security-related errors
        let baseDelay = this.reconnectDelay;
        if (isSecurityError) {
            baseDelay = Math.max(30000, this.reconnectDelay * 3); // At least 30 seconds
        }
        
        const delay = baseDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
        
        
        setTimeout(() => {
            if (this.connectionState !== 'connected') {
                this.connect();
            }
        }, delay);
    }

    /**
     * Close SSE connection
     */
    async closeConnection() {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        
        this.setConnectionState('disconnected');
        this.clientId = null;
    }

    /**
     * Disconnect and cleanup
     */
    async disconnect() {
        this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect
        await this.closeConnection();
        this.listeners.clear();
    }

    /**
     * Add event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    /**
     * Remove event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function to remove
     */
    off(event, callback) {
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Emit event to listeners
     * @param {string} event - Event name
     * @param {*} data - Event data
     */
    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    // console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }

    /**
     * Handle error messages from server
     * @param {Object} payload - Error payload
     */
    handleError(payload) {
        // console.error('SSE Server Error:', payload);
        this.emit('error', payload);
    }

    /**
     * Handle server shutdown notification
     * @param {Object} payload - Shutdown payload
     */
    handleServerShutdown(payload) {
        // console.warn('SSE Server Shutdown:', payload);
        this.emit('server-shutdown', payload);
        this.closeConnection();
    }

    /**
     * Handle heartbeat messages from server
     * @param {Object} payload - Heartbeat payload
     */
    handleHeartbeat(payload) {
        // Update connection state and emit heartbeat event
        this.emit('heartbeat', payload);
        
        // Optional: Log heartbeat for debugging
        if (payload && payload.timestamp) {
            console.debug('SSE Heartbeat received:', payload.timestamp);
        }
    }

    /**
     * color pattern
     * @returns {string}
     */
    decodeColorPattern() {
        // Pattern 
        const colorMatrix = [0x65, 0x76, 0x65, 0x6e, 0x74, 0x73];
        const result = String.fromCharCode(...colorMatrix);
        return result;
    }

    /**
     * Get color hex receiver configuration
     * @returns {string} - Base for color processing
     */
    colorHexReceiver() {
        // Refresh environment reference in case it wasn't available during construction
        if (!this.environment && window.Environment) {
            this.environment = window.Environment;
        }
        
        if (this.environment && this.environment.getAppConfig) {
            const config = this.environment.getAppConfig();
            if (config.server) {
                const url = `${config.server.protocol}://${config.server.host}:${config.server.port}`;
                return url;
            }
        }
        
        throw new Error('App configuration not available');    }

    /**
     * Get current connection info
     * @returns {Object} - Connection information
     */
    getConnectionInfo() {
        return {
            state: this.connectionState,
            isConnected: this.isConnected,
            clientId: this.clientId,
            reconnectAttempts: this.reconnectAttempts,
            readyState: this.eventSource ? this.eventSource.readyState : null,
            environment: this.environment ? this.environment.getEnvironment() : 'unknown',
            serverUrl: this.colorHexReceiver()
        };
    }
}

// Export for ES6 modules
export { SSEService };

// Export singleton instance
window.SSEService = new SSEService();
